/// <amd-module name="Wit/DeliveryPlans/Common/Services/DeliveryPlansWorkItemService" />

import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { Services } from "VSS/Platform/Context";
import { IdentityRef, JsonPatchOperation, Operation } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ClientWorkItemFields, ParentField, WorkItemEvents, observableWorkItemStackRankSorter } from "Wit/ClientWorkItemService/IClientWorkItem";
import { ClientWorkItemService, ClientWorkItemServiceEvent } from "Wit/ClientWorkItemService/IClientWorkItemService";
import { WorkItem as WitWorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient, getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { filterToArray } from "Wit/Common/Utilities/Iterator";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { reportDataProviderError, reportError } from "Wit/DeliveryPlans/Common/ErrorHandling";
import { FilterValue, IParentFilterItem } from "Wit/Filter/FilterManager";
import { getUniqueValuesForAssignedTo } from "Wit/Filter/Utilities/Utilities";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { FieldRefNames } from "../Constants";
import { IDeliveryPlansWorkItem, IIteration, IObservableDeliveryPlansWorkItem, ITeam } from "../Interfaces";
import { DeliveryPlansWorkItem, ObservableDeliveryPlansWorkItem } from "../WorkItem";
import { IDeliveryPlansIterationService } from "./DeliveryPlansIterationService";
import { IDeliveryPlansTeamService } from "./DeliveryPlansTeamService";
import { IDeliveryPlansWorkItemService, TopBottom } from "./IDeliveryPlansWorkItemService";

const updateWorkItemsContributionId = "ms.vss-work-web.update-work-items-data-provider";
class DeliveryPlansWorkItemService extends ClientWorkItemService<IObservableDeliveryPlansWorkItem> implements IDeliveryPlansWorkItemService {
    /** items in this set have changed locally and if it's in the old platform work item store, it's going to be out of date */
    private _oldPlatformStoreOutOfDateItems = new Set<number>();

    public createObservableWorkItem(workItem: IDeliveryPlansWorkItem): IObservableDeliveryPlansWorkItem {
        return new ObservableDeliveryPlansWorkItem(workItem);
    }

    public getDataSourceName(): string {
        return DeliveryPlansWorkItemService.name;
    }

    public getItemCount(): number {
        return this._items.size;
    }

    /**
     * Implementation of IBoardFilterDataSource.getIds
     * Returns only the ids of fully paged work items because partial work items cannot be filtered
     */
    public getIds(): number[] {
        const ids: number[] = [];
        for (const [id, workItem] of this._items.entries()) {
            if (!workItem.value.partial) {
                ids.push(id);
            }
        }

        return ids;
    }

    public getValue(id: number, fieldName: string) {
        return this._items.get(id)?.value.getFieldValue(fieldName);
    }

    public getVisibleFields(): string[] {
        const witIds = Array.from(this._items.keys());
        if (witIds.length === 0) {
            return [];
        }

        // work item fields are the same across every work item on the timeline
        // TODO: no they aren't
        const first = this._items.keys().next().value;
        const fields = this._items.get(first)!.value.getFields();
        return Object.keys(fields);
    }

    public getUniqueValues(fieldName: string): FilterValue[] | Promise<FilterValue[]> {
        const uniqueValues = new Set<string>();

        // special case when field value isn't a string
        const uniqueObjects: IdentityRef[] = [];
        for (const id of this._items.keys()) {
            const innerItem = this._items.get(id)!.value;
            const fieldValue = innerItem.getFieldValue<any>(fieldName);

            if (!fieldValue) {
                continue;
            }

            if (fieldName === CoreFieldRefNames.Tags) {
                const tags = (fieldValue as string).split(";");
                tags.forEach(t => uniqueValues.add(t.trim()));
            } else if (fieldName === CoreFieldRefNames.AssignedTo) {
                getUniqueValuesForAssignedTo(this.pageContext, fieldValue, uniqueValues, uniqueObjects);
            } else {
                uniqueValues.add(fieldValue.toString());
            }
        }

        return uniqueObjects.length > 0
            ? uniqueObjects.sort((a, b) => localeIgnoreCaseComparer(a.displayName, b.displayName))
            : Array.from(uniqueValues).sort(localeIgnoreCaseComparer);
    }

    public getParentItems(): IParentFilterItem[] {
        const parentFilterItems = new Map<number, IParentFilterItem>();
        for (const id of this._items.keys()) {
            const innerItem = this._items.get(id)!.value;
            const parentId = innerItem.getFieldValue<number>(CoreFieldRefNames.Parent);
            const parentName = innerItem.getFieldValue<string>(ParentField.Title);

            if (!parentId || !parentName || parentFilterItems.has(parentId)) {
                continue;
            }

            parentFilterItems.set(parentId, { id: parentId, title: parentName });
        }

        return [...parentFilterItems.values()].sort((a, b) => localeIgnoreCaseComparer(a.title, b.title));
    }

    public getAllParents(id: number): number[] {
        const parentIds = [];
        let current = this.get(id)?.value.getFieldValue<number>(CoreFieldRefNames.Parent);
        while (current) {
            parentIds.push(current);
            current = this.get(current)?.value.getFieldValue<number>(CoreFieldRefNames.Parent);
        }

        return parentIds;
    }

    public getTeamItems(team: ITeam): IObservableDeliveryPlansWorkItem[] {
        const teamService = this.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");
        return filterToArray(this._items.values(), i => teamService.teamOwnsWorkItem(team, i.value));
    }

    public getIterationItems(team: ITeam, iteration: IIteration): IObservableDeliveryPlansWorkItem[] {
        return this.getTeamItems(team)
            .filter(i => i.value.iteration.path === iteration.path)
            .sort(observableWorkItemStackRankSorter);
    }

    /** add a new work item and save it to the server */
    public async addNewWorkItem(
        projectId: string,
        workItemType: string,
        title: string,
        team: ITeam,
        iteration: IIteration,
        additionalFields: INamedFieldDataDictionary,
        direction: TopBottom
    ) {
        // fields that we send to the server
        const serverFields = {
            ...additionalFields,
            [CoreFieldRefNames.Title]: title,
            [team.teamFieldName]: team.teamFieldDefaultValue,
            [CoreFieldRefNames.IterationPath]: iteration.path
        };
        // fields that we use to populate the local WorkItem object, sending some of these to the server causes bad things
        const itemFields = {
            ...serverFields,
            [CoreFieldRefNames.TeamProject]: projectId,
            [CoreFieldRefNames.WorkItemType]: workItemType
        };

        let fields: IFieldEntry[] | undefined;
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            const fieldService = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            fields = await fieldService.loadFields();
        } else {
            const fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            fields = await fieldService.getFieldsAsync();
        }

        // create work item and add to store
        const item = new DeliveryPlansWorkItem(
            itemFields,
            this.pageContext.getService<IDeliveryPlansIterationService>("IDeliveryPlansIterationService"),
            fields
        );
        const tempId = item.id;

        if (direction === "top") {
            // fake StackRank to put the item at the top until the item is added
            item.setFieldValue(FieldRefNames.StackRank, 0);
        } else {
            item.clientSideRank = Date.now() * 1000;
        }

        item.saving = true;
        const observableItem = this.add(item);
        this._notify({ item }, ClientWorkItemServiceEvent.NewWorkItemAdded);

        // save to server
        const client = getWorkItemTrackingClient(this.pageContext);
        const patchDocument: JsonPatchOperation[] = [];
        for (const k of Object.keys(serverFields)) {
            patchDocument.push({
                op: Operation.Add, // "add",
                path: "/fields/" + k,
                from: "", // null,
                value: serverFields[k]
            });
        }

        // update work item with field values we got back from the server
        let result: WitWorkItem | undefined;
        try {
            result = await client.createWorkItem(patchDocument, projectId, workItemType);
            item.id = result.id;
            item.setFieldValue(CoreFieldRefNames.Rev, result.rev);
            for (const k of Object.keys(result.fields)) {
                item.setFieldValue(k, result.fields[k]);
            }
            this._afterItemCreation(observableItem, tempId, team, direction);
        } catch (ex) {
            if (isFeatureEnabled(this.pageContext, "ms.vss-work-web.new-boards-hub-feature") && ex.message?.includes("TF401320")) {
                // item was invalid
                this._openWorkItemDialogForNewItem(observableItem, team, direction);
            } else {
                reportError(this.pageContext, "DeliveryPlansWorkItemService.addNewWorkItem", ex);
            }
        }

        observableItem.notify();
    }

    private _afterItemCreation(observableItem: IObservableDeliveryPlansWorkItem, tempId: number, team: ITeam, direction: TopBottom) {
        const item = observableItem.value;
        item.saving = false;
        this._items.delete(tempId);
        this._items.set(item.id, observableItem);

        // if we want the new item to be at the top of the iteration, we have to reorder it
        // items will show up at the bottom by default
        if (direction === "top") {
            this.reorderInIterationToEnd(observableItem, team, "top");
        }
    }

    /** used when a newly-added item is invalid */
    private _openWorkItemDialogForNewItem(observableItem: IObservableDeliveryPlansWorkItem, team: ITeam, direction: TopBottom) {
        const service = this.pageContext.getService<IWorkItemService>("work-item-service");
        const item = observableItem.value;
        service.openWorkItemWithProps({
            initialValues: {
                ...item.getFields()
            },
            onLegacyDismiss: () => {
                if (item.id > 0) {
                    // item was saved, nothing to do
                    return;
                }
                // item was not saved, remove it
                this._items.delete(item.id);
                this._notify({}, ClientWorkItemServiceEvent.WorkItemRemoved);
            },
            onLegacySave: legacyWorkItem => {
                if (item.id > 0) {
                    // item has already been saved once so we don't have to do anything special
                    this._onWorkItemSaved(legacyWorkItem);
                    return;
                }
                const fields = this.getVisibleFields();
                const tempId = item.id;
                for (const f of fields) {
                    item.setFieldValue(f, legacyWorkItem.getFieldValue(f));
                }
                this._afterItemCreation(observableItem, tempId, team, direction);

                observableItem.notify();
            },
            workItemTypeName: item.getRequiredFieldValue<string>(CoreFieldRefNames.WorkItemType)
        });
    }

    /** persist changes to a work item to the server */
    public async updateWorkItem(item: IDeliveryPlansWorkItem | number, fields: INamedFieldDataDictionary) {
        let observableWorkItem: IObservableDeliveryPlansWorkItem | undefined;
        if (typeof item === "number") {
            observableWorkItem = this.get(item);
            if (!observableWorkItem) {
                throw Error(`Could not find work item ${item}`);
            }
            item = observableWorkItem.value;
        } else {
            observableWorkItem = this.get(item.id);
        }

        if (observableWorkItem && observableWorkItem.value === item) {
            for (const fieldName of Object.keys(fields)) {
                item.setFieldValue(fieldName, fields[fieldName]);
            }

            item.saving = true;
            observableWorkItem.notify();
        } else {
            throw Error("Expected work item to match the one in store.");
        }

        const updatePackage = [
            {
                id: item.id,
                rev: item.getFieldValue("System.Rev"),
                fields
            }
        ];

        await this._save(observableWorkItem, updatePackage);
    }

    public async reorderInIterationToEnd(item: IObservableDeliveryPlansWorkItem, team: ITeam, direction: TopBottom): Promise<void> {
        const iterationItems = this.getIterationItems(team, item.value.iteration);

        if (iterationItems.length <= 1) {
            // it's easy to sort a list of length 1
            return;
        }
        if (direction === "top") {
            await this.reorderWorkItems(
                item.value.id,
                team.projectId,
                team.id,
                undefined,
                iterationItems[0].value.id,
                item.value.iteration.id,
                undefined,
                team.orderByField,
                true
            );
        } else {
            await this.reorderWorkItems(
                item.value.id,
                team.projectId,
                team.id,
                iterationItems[iterationItems.length - 1].value.id,
                undefined,
                item.value.iteration.id,
                undefined,
                team.orderByField,
                true
            );
        }
    }

    public openWorkItemDialog(workItemId: number, onSave?: (workItem: any) => void) {
        if (workItemId > 0) {
            const service = this.pageContext.getService<IWorkItemService>("work-item-service");
            const save = (workItem: any) => {
                this._onWorkItemSaved(workItem);
                onSave && onSave(workItem);
            };

            const deleteItem = (workItem: any) => {
                this.clear(workItem.id);
                this._notify({}, ClientWorkItemServiceEvent.WorkItemRemoved);
            };
            service.openWorkItem(workItemId, { save, deleteItem }, /* tryGetLatest */ true);
            this._oldPlatformStoreOutOfDateItems.delete(workItemId);
        }
    }

    public async loadWorkItems(project: string, workItemIds: number[], fieldReferenceNames: string[]): Promise<void> {
        const service = this.pageContext.getService<IWorkItemService>("work-item-service");
        try {
            const resolvedWorkItems = (await Promise.all(service.loadWorkItems(project, workItemIds, fieldReferenceNames))).flat();
            for (const workItemData of resolvedWorkItems) {
                const item = this._items.get(workItemData.id);
                if (item) {
                    item.value.setFieldValues(workItemData.fields);
                    item.value.partial = false;
                    this._notify({ item: item.value }, ClientWorkItemServiceEvent.PartialWorkItemUpdated);
                }
            }
        } catch (ex) {
            reportError(this.pageContext, "DeliveryPlansWorkItemService.loadWorkItems", ex);
        }
    }

    private async _save(
        observableWorkItem: IObservableDeliveryPlansWorkItem,
        updatePackage: { fields: INamedFieldDataDictionary; [key: string]: any }[]
    ) {
        const data = {
            updatePackage: JSON.stringify(updatePackage)
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const update = await contributionService.getDataAsync<IWorkItemUpdate>(updateWorkItemsContributionId, data, /* ignoreCache */ true);
        if (update) {
            await this._handleUpdate(update);
        } else {
            reportDataProviderError(this.pageContext, "DeliveryPlansWorkItemService._save", updateWorkItemsContributionId);
        }

        observableWorkItem.value.saving = false;
        observableWorkItem.notify();
    }

    /**
     * Updates a work item based on updates we received from the server.
     */
    private async _handleUpdate(update: IWorkItemUpdate): Promise<void> {
        let fields: IFieldEntry[] | undefined;
        let fieldService: IWorkItemFieldService | IWorkItemFieldService2;
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            fieldService = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            fields = await fieldService.loadFields();
        } else {
            fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            fields = await fieldService.getFieldsAsync();
        }

        if (!fields) {
            return;
        }

        for (const item of update.data) {
            const id = item.id;
            const workItemObservable = this.get(id);
            if (!workItemObservable) {
                console.warn(`Could not find work item ${id} in data store.`);
                continue;
            }
            const workItem = workItemObservable.value;
            for (const fieldId of Object.keys(item.fields)) {
                const referenceName = fieldService.getField(Number(fieldId))?.referenceName;
                if (!referenceName) {
                    console.warn(`Could not find field ${fieldId}.`);
                    continue;
                }

                const newValue = item.fields[fieldId];
                if (workItem.getFieldValue(referenceName) !== newValue) {
                    workItem.setFieldValue(referenceName, newValue);
                }
            }
        }
    }

    /**
     * Called when any work item is updated.
     */
    protected _workItemSubscriber = (workItem: IDeliveryPlansWorkItem, action: WorkItemEvents) => {
        if (action !== WorkItemEvents.Set) {
            return;
        }

        this._notify({ item: workItem }, ClientWorkItemServiceEvent.WorkItemUpdated);
        this._oldPlatformStoreOutOfDateItems.add(workItem.id);
    };

    /**
     * Update from a work item that was saved from the old work item form
     * @param workItem old WIT OM WorkItem object
     */
    private _onWorkItemSaved = (workItem: any) => {
        const storedItem = this.get(workItem.id);
        if (!storedItem) {
            this.updateParentFieldTitle(workItem.id, workItem.getFieldValue(CoreFieldRefNames.Title));
            this._notify({}, ClientWorkItemServiceEvent.WorkItemUpdated);
            return;
        }

        const currentWIFields = storedItem.value.getFields();

        // special case: parent may need to be loaded from the server
        const currentParent = currentWIFields[CoreFieldRefNames.Parent];
        const newParent = workItem.getFieldValue(CoreFieldRefNames.Parent);

        Object.keys(currentWIFields).forEach(fieldReferenceName => {
            if (ClientWorkItemFields.has(fieldReferenceName)) {
                return;
            }

            const currentValue = currentWIFields[fieldReferenceName];
            const newValue = workItem.getFieldValue(fieldReferenceName);
            if ((currentValue || newValue) && currentValue !== newValue) {
                storedItem.value.setFieldValue(fieldReferenceName, newValue);
                if (fieldReferenceName === CoreFieldRefNames.Title) {
                    this.updateParentFieldTitle(workItem.id, newValue);
                }
            }
        });

        if ((currentParent || newParent) && currentParent !== newParent) {
            const typedParentId = newParent as number | undefined;
            this._updateWorkItemParentFields(storedItem.value, typedParentId).then(() => storedItem.notify());
        } else {
            storedItem.notify();
        }
        this._oldPlatformStoreOutOfDateItems.delete(storedItem.value.id);
    };

    protected async _updateWorkItemParentFields(workItem: IDeliveryPlansWorkItem, parentId?: number) {
        const fieldUpdates: { [field: string]: unknown } = {};
        if (parentId) {
            // first check if it's already on the page
            const parentItem = this.get(parentId);
            let parentFieldValues = parentItem?.value.getFields();
            if (!parentFieldValues) {
                const pagedParentItem = await this._pageWorkItem(parentId, [CoreFieldRefNames.Title, CoreFieldRefNames.WorkItemType]);
                parentFieldValues = pagedParentItem.fields;
            }

            fieldUpdates[ParentField.Title] = parentFieldValues[CoreFieldRefNames.Title];
            fieldUpdates[ParentField.WorkItemType] = parentFieldValues[CoreFieldRefNames.WorkItemType];
        } else {
            fieldUpdates[ParentField.Title] = "";
            fieldUpdates[ParentField.WorkItemType] = "";
        }

        workItem.setFieldValues(fieldUpdates);
    }

    private async _pageWorkItem(workItemId: number, fieldNames: string[]): Promise<WitWorkItem> {
        const witClient = this.pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        return witClient.getWorkItem(workItemId, undefined, fieldNames);
    }
}

Services.add("IDeliveryPlansWorkItemService", { serviceFactory: DeliveryPlansWorkItemService });

/**
 * attempt to describe the return type of ms.vss-work-web.update-work-items-data-provider
 */
interface IWorkItemUpdate {
    data: { id: number; fields: { [fieldRefName: string]: any } }[];
}
